import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import StaffService from "services/staffService";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { FormButton } from "reusableComponents";

const CalendarUsersView = () => {
  const { t } = useTranslation("global");
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: false,
    email: false,
  });
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchUsers();
    } else {
      navigate("/login");
    }
  }, [token, navigate]);

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await StaffService.getStaff(token);
      setUsers(response.data);
    } catch (err) {
      if (err.message === "Authentication failed") {
        navigate("/login");
      } else {
        setError(t("errors.fetchUsers"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim(),
      email: !formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email),
    };
    setFormErrors(errors);
    return !errors.name && !errors.email;
  };

  const handleAddUser = () => {
    setIsAdding(true);
    setEditingUser(null);
    setFormData({
      name: "",
      email: "",
      service: "",
    });
    setFormErrors({
      name: false,
      email: false,
    });
  };

  const handleEditUser = (user) => {
    setIsAdding(false);
    setEditingUser(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      service: user.service,
    });
    setFormErrors({
      name: false,
      email: false,
    });
  };

  const handleCancel = () => {
    setEditingUser(null);
    setIsAdding(false);
    setFormErrors({
      name: false,
      email: false,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let response;
      if (editingUser) {
        response = await StaffService.updateStaff(token, editingUser, formData);
      } else {
        response = await StaffService.createStaff(token, formData);
      }

      setUsers(response.data);
      handleCancel();
    } catch (err) {
      if (err.message === "Authentication failed") {
        navigate("/login");
      } else {
        setError(t("errors.saveUser"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const isSubmitDisabled = () => {
    if (isAdding) {
      return !formData.name.trim() || !formData.email.trim();
    }
    if (editingUser) {
      const user = users.find(u => u.id === editingUser);
      return (
        formData.name === user.name &&
        formData.email === user.email &&
        formData.service === user.service
      );
    }
    return false;
  };

  return (
    <section className={styles.calendarUsers}>
      <div className={styles.header}>
        <div className={styles.colHalf}>
          <h2 className={styles.calendarTitle}>{t("calendar.users.title")}</h2>
        </div>
        <div className={styles.addBtnContainer}>
          <FormButton
            type="button"
            onClick={handleAddUser}
            label={`+ ${t("calendar.users.addUser")}`}
          />
        </div>
      </div>

      <div className={styles.usersList}>
        <form onSubmit={handleSubmit} className={styles.horizontalForm}>
          <table className={`${styles.table} ${styles.usersTable}`}>
            <thead>
              <tr>
                <th>{t("calendar.users.name")}</th>
                <th>{t("calendar.users.email")}</th>
                <th>{t("calendar.users.service")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isAdding && (
                <tr className={styles.rowNew}>
                  <td>
                    <input
                      type="text"
                      className={`${styles.formControl} ${formErrors.name ? styles.error : ''}`}
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      autoComplete="off"
                      required
                    />
                    {formErrors.name && <div className={styles.errorMessage}>{t("errors.required")}</div>}
                  </td>
                  <td>
                    <input
                      type="email"
                      className={`${styles.formControl} ${formErrors.email ? styles.error : ''}`}
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                    {formErrors.email && <div className={styles.errorMessage}>
                      {!formData.email.trim() ? t("errors.required") : t("errors.invalidEmail")}
                    </div>}
                  </td>
                  <td>
                    <input
                      type="text"
                      className={styles.formControl}
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                    />
                  </td>
                  <td>
                    <div className={styles.actionButtons}>
                      <button
                        type="button"
                        className={`${styles.btn} ${styles.btnInfo} ${styles.btnSm} ${styles.btnBlock}`}
                        onClick={handleCancel}
                      >
                        {t("buttons.cancel")}
                      </button>
                      <button
                        type="submit"
                        className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm} ${styles.btnBlock}`}
                        disabled={isSubmitDisabled()}
                      >
                        {t("buttons.confirm")}
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              {users.map((user, index) => (
                <tr
                  key={user.id}
                  className={index % 2 === 0 ? styles.rowEven : styles.rowOdd}
                >
                  <td>
                    {editingUser !== user.id ? (
                      user.name
                    ) : (
                      <input
                        type="text"
                        className={`${styles.formControl} ${formErrors.name ? styles.error : ''}`}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    )}
                  </td>
                  <td>
                    {editingUser !== user.id ? (
                      <a className={styles.userEmail} href={`mailto:${user.email}`}>{user.email}</a>
                    ) : (
                      <input
                        type="email"
                        className={`${styles.formControl} ${formErrors.email ? styles.error : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    )}
                  </td>
                  <td>
                    {editingUser !== user.id ? (
                      user.service
                    ) : (
                      <input
                        type="text"
                        className={styles.formControl}
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                      />
                    )}
                  </td>
                  <td>
                    {editingUser !== user.id ? (
                      <button
                        onClick={() => handleEditUser(user)}
                        className={styles.iconWrapper}
                      >
                        <span className={styles.editIcon}></span>
                      </button>
                    ) : (
                      <div className={styles.actionButtons}>
                        <button
                          type="button"
                          className={`${styles.btn} ${styles.btnInfo} ${styles.btnSm} ${styles.btnBlock}`}
                          onClick={handleCancel}
                        >
                          {t("buttons.cancel")}
                        </button>
                        <button
                          type="submit"
                          className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm} ${styles.btnBlock}`}
                          disabled={isSubmitDisabled()}
                        >
                          {t("buttons.confirm")}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </form>
      </div>
    </section>
  );
};

export default CalendarUsersView;